import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Send, Calendar, Clock, Flag, MoreVertical, ShieldAlert } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ProposeSetTestModal from "@/components/app/ProposeSetTestModal";
import ReportModal from "@/components/app/ReportModal";

const fmtRemaining = (sec) => {
  if (sec <= 0) return "EXPIRED";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}h`;
};

const Chat = () => {
  const { matchId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [match, setMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [tick, setTick] = useState(0);
  const scrollRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [m, msgs] = await Promise.all([
        api.get(`/matches/${matchId}`),
        api.get("/messages", { params: { match_id: matchId } }),
      ]);
      setMatch(m.data);
      setMessages(msgs.data);
      setTimeout(
        () => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }),
        50
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load chat.");
      if (err?.response?.status === 404) nav("/app/matches");
    }
  }, [matchId, nav]);

  useEffect(() => {
    loadAll();
    const poll = setInterval(loadAll, 4000);
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => {
      clearInterval(poll);
      clearInterval(t);
    };
  }, [loadAll]);

  const send = async (e) => {
    e?.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { match_id: matchId, body: body.trim() });
      setMessages((m) => [...m, data]);
      setBody("");
      setTimeout(
        () => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }),
        20
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send.");
    } finally {
      setSending(false);
    }
  };

  if (!match) {
    return (
      <div className="text-center py-20 text-neutral-500" data-testid="chat-loading">
        Loading conversation…
      </div>
    );
  }

  const seconds = Math.max(0, match.seconds_remaining - tick);
  const expired = match.status === "expired" || (seconds <= 0 && match.status !== "scheduled");
  const scheduled = match.status === "scheduled";

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)]" data-testid="chat-page">
      {/* Match header */}
      <div className="flex items-center gap-3 pb-4 border-b border-white/5">
        <button
          onClick={() => nav("/app/matches")}
          data-testid="chat-back-btn"
          className="text-neutral-400 hover:text-white p-1"
          aria-label="Back"
        >
          <ArrowLeft size={20} />
        </button>
        <img
          src={match.other_user.photo_url}
          alt={match.other_user.name}
          className="w-11 h-11 rounded-full object-cover border border-white/10"
        />
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-2xl leading-none">
            {match.other_user.name}
            {match.other_user.age && (
              <span className="text-neutral-400 text-lg">, {match.other_user.age}</span>
            )}
          </h2>
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">
            {(match.other_user.training_disciplines || []).slice(0, 3).join(" · ")}
          </div>
        </div>
        <div className="text-right">
          {scheduled ? (
            <span className="px-2 py-1 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
              <Calendar size={10} /> Booked
            </span>
          ) : expired ? (
            <span className="px-2 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-bold uppercase tracking-wider">
              Expired
            </span>
          ) : (
            <div data-testid="chat-countdown" className="font-heading text-xl text-[#C6FF00] tabular-nums leading-none flex items-center gap-1">
              <Clock size={14} /> {fmtRemaining(seconds)}
            </div>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu((v) => !v)}
            data-testid="chat-menu-btn"
            className="text-neutral-400 hover:text-white p-1.5 rounded-md"
            aria-label="Menu"
          >
            <MoreVertical size={18} />
          </button>
          {showMenu && (
            <div
              className="absolute right-0 top-full mt-2 z-30 w-44 rounded-xl bg-[#111] border border-white/10 overflow-hidden shadow-2xl"
              data-testid="chat-menu"
            >
              <button
                onClick={() => {
                  setShowReport(true);
                  setShowMenu(false);
                }}
                data-testid="chat-report-btn"
                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs uppercase tracking-wider text-neutral-200 hover:bg-white/5 hover:text-red-400"
              >
                <Flag size={14} /> Report user
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto py-4 space-y-2"
        data-testid="chat-messages"
      >
        {messages.length === 0 && (
          <div className="text-center text-neutral-500 text-sm pt-12">
            <p>Say something. Plan a session. The clock is ticking.</p>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div
              key={m.id}
              data-testid={`chat-msg-${m.id}`}
              className={`flex ${mine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  mine
                    ? "bg-[#C6FF00] text-[#0A0A0A] font-medium"
                    : "bg-white/5 text-white border border-white/10"
                }`}
              >
                {m.body}
              </div>
            </div>
          );
        })}
      </div>

      {/* Schedule banner */}
      {!expired && (
        <button
          onClick={() => setShowSchedule(true)}
          data-testid="chat-schedule-btn"
          className="mb-3 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-[#C6FF00]/40 hover:border-[#C6FF00] hover:bg-[#C6FF00]/5 text-[#C6FF00] font-semibold text-xs uppercase tracking-[0.2em] transition-all"
        >
          <Calendar size={14} /> Propose a Set Test
        </button>
      )}

      {/* Composer */}
      {expired ? (
        <div className="p-3 rounded-xl bg-[#111] border border-white/5 text-center text-xs uppercase tracking-[0.2em] text-neutral-500">
          This match expired. Time waits for no rep.
        </div>
      ) : (
        <form onSubmit={send} className="flex items-center gap-2" data-testid="chat-composer">
          <input
            data-testid="chat-input"
            type="text"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type a message…"
            className="flex-1 bg-[#111] border border-white/10 rounded-full px-5 py-3 text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#C6FF00] transition-colors"
          />
          <button
            type="submit"
            data-testid="chat-send-btn"
            disabled={sending || !body.trim()}
            className="w-12 h-12 rounded-full bg-[#C6FF00] text-[#0A0A0A] flex items-center justify-center disabled:opacity-50 hover:scale-105 transition-transform"
            aria-label="Send"
          >
            <Send size={18} />
          </button>
        </form>
      )}

      {showSchedule && (
        <ProposeSetTestModal
          match={match}
          onClose={() => setShowSchedule(false)}
          onProposed={() => {
            setShowSchedule(false);
            loadAll();
            toast.success("Set Test sent. Awaiting acceptance.");
            nav("/app/schedule");
          }}
        />
      )}
      {showReport && (
        <ReportModal
          targetUser={match.other_user}
          onClose={() => setShowReport(false)}
          onSubmitted={() => {
            setShowReport(false);
            toast.success("Report submitted. Trust team will review within 24h.");
          }}
        />
      )}
    </div>
  );
};

const ChatList = () => {
  // When /app/chat (no id) is hit, redirect-list to matches
  return (
    <div className="text-center py-16" data-testid="chat-list-redirect">
      <p className="text-sm text-neutral-400 mb-4">Pick a match to start chatting.</p>
      <Link
        to="/app/matches"
        className="bg-[#C6FF00] text-[#0A0A0A] font-bold text-xs uppercase tracking-[0.25em] px-6 py-3 rounded-md"
      >
        See Matches
      </Link>
    </div>
  );
};

export { ChatList };
export default Chat;
